import React, { ComponentProps, FC, useState } from 'react';
import Divider from '../../../ui/Divider/Divider';
import Paragraph from '../../../ui/Paragraph/Paragraph';
import Button from '../../../ui/Button/BlockButton';
import LinkIcon from '../../../ui/icons/Link/Link';
import CopyIcon from '../../../ui/icons/Copy/Copy';
import { useTranslations } from '../../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../../hooks/Analytics';
import { useTheme } from '../../../../hooks/useTheme';
import styles from './SharingOptions.module.css';

interface SharingOptionsProps {
  featureName?: string;
  giftTrackingSharingMessageSubject: string;
  giftTrackingSharingMessageBody: string;
  giftTrackingLink: string;
}

const SharingOptions = ({
  giftTrackingSharingMessageSubject,
  giftTrackingSharingMessageBody,
  featureName,
  giftTrackingLink,
}: SharingOptionsProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const logEvent = useAnalyticsPost();

  const {
    bodytextDefaultNone: bodyTextStyle,
    inputtextinputsNoneNone: inputStyle,
  } = useTheme();

  const {
    giftTrackingSendEmailPrompt,
    giftTrackingSendEmailButtonText,
    giftTrackingCopyLinkPrompt,
    giftTrackingCopyLinkSuccessMessage,
  } = useTranslations();

  const handleSendEmail = () => {
    window.open(
      `mailto:?subject=${giftTrackingSharingMessageSubject}&body=${giftTrackingSharingMessageBody}`,
    );

    logEvent({
      clickCategory: NarvarClickSubTypes.SEND_EMAIL,
      clickSubType: NarvarClickSubTypes.GIFT_TRACKING,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      eventFeature: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
    });
  };

  const handleCopyLink = async () => {
    try {
      await window.navigator.clipboard.writeText(giftTrackingLink);

      logEvent({
        clickCategory: NarvarClickSubTypes.COPY_LINK,
        clickSubType: NarvarClickSubTypes.GIFT_TRACKING,
        clickType: NarvarClickTypes.INTERACTION,
        eventComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
        eventFeature: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
        eventType: NarvarEventTypes.CLICK,
        feature: featureName,
        pageComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      });

      setIsCopied(true);
    } catch (err) {
      // TODO: Round out the experience with a proper error state
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  return (
    <>
      <Divider />
      <div className={styles.shareOptionsContainer}>
        <div className={styles.shareOptionsButton}>
          <Paragraph>{giftTrackingSendEmailPrompt}</Paragraph>
          <Button big onClick={handleSendEmail}>
            {giftTrackingSendEmailButtonText}
          </Button>
        </div>
        <div className={styles.shareOptionsForm}>
          <Paragraph>{giftTrackingCopyLinkPrompt}</Paragraph>
          <div className={styles.linkBox}>
            <LinkIcon s={{ marginLeft: '0.25rem' }} />
            <input
              className={styles.linkText}
              value={giftTrackingLink}
              style={inputStyle}
              onFocus={(e) => e.target.select()}
              readOnly
            />
            <button
              className={styles.copyLinkButton}
              aria-label={giftTrackingCopyLinkPrompt || 'Copy Link'}
              onClick={handleCopyLink}
            >
              <CopyIcon />
            </button>
          </div>
          <div role="region" aria-live="polite">
            {isCopied && (
              <div className={styles.copyLinkSuccessText} style={bodyTextStyle}>
                {giftTrackingCopyLinkSuccessMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SharingOptions;
